import React from 'react';
import { useTranslation } from 'react-i18next';

const RANKS_ORDER = ['prep_cook', 'commis_chef', 'head_cook', 'sous_chef', 'head_chef'];
const RANK_EMOJIS = { prep_cook: '🧑‍🍳', commis_chef: '👨‍🍳', head_cook: '🍳', sous_chef: '🥘', head_chef: '⭐' };
const RANK_THRESHOLDS = { prep_cook: 0, commis_chef: 25, head_cook: 55, sous_chef: 80, head_chef: 100 };

function getEncouragement(t, rankData) {
  if (!rankData.next_rank) {
    return t('rank.encourageMax');
  }
  const gap = rankData.next_rank_threshold - rankData.xp;
  if (gap <= 10) {
    return t('rank.encourageClose', { points: gap, rank: t(`rank.${rankData.next_rank}`) });
  }
  return t('rank.encourageKeepGoing', { rank: t(`rank.${rankData.next_rank}`) });
}

export default function RankDetail({ rankData, onClose }) {
  const { t } = useTranslation();

  if (!rankData) return null;

  return (
    <div className="rank-detail-overlay" onClick={onClose}>
      <div className="rank-detail-panel" onClick={e => e.stopPropagation()}>
        <button className="rank-detail-close" onClick={onClose}>×</button>

        <div className="rank-detail-header">
          <span className="rank-detail-emoji">{rankData.rank_emoji}</span>
          <h2 className="rank-detail-title">{t(`rank.${rankData.rank}`)}</h2>
        </div>

        <p className="rank-encouragement">{getEncouragement(t, rankData)}</p>

        <div className="rank-ladder">
          <h3>{t('rank.allRanks')}</h3>
          {(() => {
            const dist = rankData.league_distribution || [];
            const maxPct = Math.max(...dist.map(d => d.pct), 1);
            // Compute marker position: XP progress within the current rank tier
            const rankIdx = RANKS_ORDER.indexOf(rankData.rank);
            const currentThreshold = RANK_THRESHOLDS[rankData.rank] || 0;
            const nextThreshold = rankData.next_rank_threshold;
            const markerPct = nextThreshold
              ? Math.round(((rankData.xp - currentThreshold) / (nextThreshold - currentThreshold)) * 100)
              : 100;
            return [...RANKS_ORDER].reverse().map(key => {
              const isCurrent = key === rankData.rank;
              const entry = dist.find(d => d.rank === key);
              const pct = entry ? entry.pct : 0;
              const barWidth = Math.round((pct / maxPct) * 85);
              return (
                <div key={key} className={`rank-ladder-row${isCurrent ? ' current' : ''}`}>
                  <span className="rank-ladder-emoji">{RANK_EMOJIS[key]}</span>
                  <span className="rank-ladder-name">{t(`rank.${key}`)}</span>
                  <div className="rank-ladder-bar">
                    <div
                      className={`rank-ladder-fill${isCurrent ? ' current' : ''}`}
                      style={{ width: `${barWidth}%` }}
                    />
                    {isCurrent && <div className="rank-ladder-marker" style={{ left: `${markerPct}%` }} />}
                  </div>
                </div>
              );
            });
          })()}
        </div>

        <div className="rank-weeks">
          <h3>{t('rank.weekBreakdown')}</h3>
          {rankData.weeks.length === 0 ? (
            <p className="rank-no-data">{t('rank.noData')}</p>
          ) : (
            rankData.weeks.map(w => (
              <div key={w.week} className="rank-week-row">
                <span className="rank-week-label">{w.week}</span>
                <div className="rank-week-bar">
                  <div
                    className="rank-week-fill"
                    style={{ width: `${Math.round((w.filled / w.total) * 100)}%` }}
                  />
                </div>
                <span className="rank-week-stats">{w.filled}/{w.total}</span>
                <span className="rank-week-pts">+{w.points}</span>
              </div>
            ))
          )}
        </div>

        {rankData.bonuses && rankData.bonuses.length > 0 && (
          <div className="rank-bonuses">
            <h3>{t('rank.bonuses')}</h3>
            {rankData.bonuses.map(b => (
              <div key={b.type} className="rank-bonus-row">
                <span>{t(`rank.bonus_${b.type}`)}</span>
                <span className="rank-week-pts">+{b.points}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
